import { useCallback, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  useGridApiRef,
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridPreProcessEditCellProps,
  GridRowOrderChangeParams,
} from "@mui/x-data-grid-pro";
import { WarningDialog } from "../../../components/dialogs/WarningDialog";
import { SelectEditCell } from "../../../components/grids/SelectEditCell";
import {
  DataTypeName,
  applyDifference,
  swapEntries,
  equalsCI,
  BaseDataTypeNames,
} from "../../../features/data/data-types";
import {
  Category,
  ProductField,
  reservedFieldNames,
  reservedFields,
} from "../../../features/products/product";
import { IconTypes } from "../../../components/icons.types";
import { useSnackbar } from "notistack";
import { journeyManager } from "../../../features/guide/journeyManager";
import QuickFieldAdd from "./QuickFieldAdd ";

interface CategoryFieldsProps {
  category: Category;
  onChange: (category: Category) => void;
}

export const CategoryFields = ({ category, onChange }: CategoryFieldsProps) => {
  const [toDeleteField, setDeleteField] = useState<ProductField>();
  const apiFieldRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const addField = useCallback(
    (f: ProductField) => {
      f.fieldId = -category.fields.length - 1;
      const fields = [...category.fields, f];
      onChange({ ...category, fields });
      journeyManager.updateJourneyProgress("product_setup", "add_fields");
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (apiFieldRef.current?.getRowMode(f.fieldId) === "view")
            apiFieldRef.current.startRowEditMode({ id: f.fieldId });
          apiFieldRef.current.startCellEditMode({
            id: f.fieldId,
            field: "name",
          });
        }, 100);
      });
      return f;
    },
    [apiFieldRef, category, onChange]
  );

  const processFieldUpdate = useCallback(
    (newRow, oldRow) => {
      const fields = [...category.fields];
      const editedField = fields.find((f) => f.fieldId === newRow.id);
      if (editedField) {
        const name = newRow.name?.trim().toLowerCase();
        let error = "";

        if (reservedFieldNames.some((f) => f.toLowerCase() === name)) {
          error = newRow.name + " is reserved field name";
        }
        if (
          fields.some(
            (f) => f.fieldId !== newRow.id && f.name.toLowerCase() === name
          )
        ) {
          error = "Field name must be unique: " + newRow.name;
        }
        if (error) {
          enqueueSnackbar(error, {
            variant: "error",
          });
          apiFieldRef.current.startCellEditMode({
            id: newRow.id,
            field: "name",
          });
          return oldRow;
        }
        applyDifference(editedField, newRow, oldRow);
        onChange({ ...category, fields });
      }
      return newRow;
    },
    [apiFieldRef, category, enqueueSnackbar, onChange]
  );

  const deleteField = useCallback(
    (id) => {
      const fields = category.fields.filter((f) => f.fieldId !== id);
      onChange({ ...category, fields });
      setDeleteField(null);
    },
    [category, onChange]
  );

  const fieldColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Field",
        flex: 1,
        minWidth: 100,
        editable: true,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value.length < 1;
          return { ...params.props, error: hasError };
        },
      },
      {
        field: "dataType",
        headerName: "Data Type",
        editable: true,
        renderEditCell: (params) => (
          <SelectEditCell
            {...params}
            entries={BaseDataTypeNames.map((t) => ({ name: t, value: t }))}
          />
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 35,
        getActions: (params) => {
          const df = category.fields.find((f) => f.fieldId === params.id);
          return df && !reservedFields.some((f) => f.name === df.name)
            ? [
                <GridActionsCellItem
                  icon={<IconTypes.DeleteAction />}
                  label="Delete field"
                  key="1"
                  onClick={() => {
                    if (df.fieldId > 0) setDeleteField(df);
                    else deleteField(df.fieldId);
                  }}
                />,
              ]
            : [];
        },
      },
    ],
    [deleteField, category?.fields]
  );

  const fieldToRow = (f: ProductField) => ({
    id: f.fieldId,
    __reorder__: f.name,
    ...f,
  });

  const fieldRows = useMemo(() => {
    return category?.fields.map((f) => fieldToRow(f)) ?? [];
  }, [category?.fields]);

  const handleFieldOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      const fields = swapEntries(
        category.fields,
        params.oldIndex,
        params.targetIndex
      );
      onChange({ ...category, fields });
    },
    [category, onChange]
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          onClick={() => addField({ name: "", dataType: DataTypeName.Text })}
          size="small"
          variant="outlined"
        >
          + Add new field
        </Button>
        <Typography
          variant="body2"
          color="text.secondary"
          fontSize="small"
          sx={{ my: 1 }}
        >
          Fields added here will be available for all products in this category.
          For unique product variations, consider using variants instead.
        </Typography>
        <QuickFieldAdd onAddField={addField} />
      </Box>
      <DataGridPro
        apiRef={apiFieldRef}
        rows={fieldRows}
        columns={fieldColumns}
        editMode="row"
        rowReordering
        onRowOrderChange={handleFieldOrderChange}
        hideFooter
        onRowClick={(params) => {
          if (apiFieldRef.current?.getRowMode(params.id) === "view")
            apiFieldRef.current.startRowEditMode({ id: params.id });
        }}
        isCellEditable={(params) =>
          !reservedFields.some((f) => equalsCI(f.name, params.row.name))
        }
        processRowUpdate={(newRow, oldRow) =>
          processFieldUpdate(newRow, oldRow)
        }
      />
      <WarningDialog
        open={!!toDeleteField}
        onClose={() => {
          setDeleteField(null);
        }}
        onConfirm={() => deleteField(toDeleteField.fieldId)}
        title={`Delete field ${toDeleteField?.name ?? ""}?`}
        action="Delete"
        color="error"
      >
        The field will be deleted from all products in this category
      </WarningDialog>
    </Box>
  );
};
